import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Clock, Facebook, Globe, Calendar, ExternalLink } from 'lucide-react';
import ScheduleModal from './ScheduleModal';

const Footer = () => {
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  return (
    <>
    <footer className="bg-gray-900 text-gray-300 pb-24 md:pb-0">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          
          {/* Brand & About */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <img 
                src="/cmc_logo.png" 
                alt="Community Medical Center of West Volusia" 
                className="h-20 w-auto"
              />
            </div>
            <p className="text-base text-gray-400 mb-5 leading-relaxed">
              A Rural Health Clinic providing accessible, quality pediatric and internal medicine services to West Volusia, Florida.
            </p>
            <div className="flex items-center gap-2 text-teal-400 font-medium mb-5">
              <Globe size={18} />
              <span>Se Habla Español</span>
            </div>
            <div className="flex gap-4">
              <a 
                href="https://www.facebook.com/CommunityMedicalCenterOfWestVolusia" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-gray-800 hover:bg-blue-600 p-3 rounded-lg transition-colors"
                aria-label="Visit our Facebook page"
              >
                <Facebook size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-bold text-lg mb-6 tracking-wide">Quick Links</h3>
            <ul className="space-y-3">
              {[
                { to: '/', label: 'Home' },
                { to: '/about', label: 'About Us' },
                { to: '/services', label: 'Services' },
                { to: '/providers', label: 'Our Providers' },
                { to: '/sliding-fee', label: 'Payment Plans' },
                { to: '/preceptorship', label: 'NP Preceptorships' },
                { to: '/contact', label: 'Contact' },
              ].map((link) => (
                <li key={link.to}>
                  <Link 
                    to={link.to} 
                    className="text-gray-400 hover:text-white transition-colors font-medium"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
            <a
              href="https://mycw249.ecwcloud.com/portal30300/jsp/login.jsp"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-patient-portal-link"
              className="inline-flex items-center gap-2 text-blue-400 hover:text-white transition-colors font-medium mt-4"
            >
              <ExternalLink size={16} />
              Patient Portal &amp; Bill Pay
            </a>
          </div>
          <div>
            <h3 className="text-white font-bold text-lg mb-6 tracking-wide">Orange City Office</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={20} className="text-blue-400 mt-0.5 flex-shrink-0" />
                <address className="not-italic text-gray-400">
                  <span className="text-white font-medium">810 Commed Blvd.</span><br />
                  Orange City, FL 32763
                </address>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={20} className="text-blue-400 flex-shrink-0" />
                <a 
                  href="tel:386-775-1792" 
                  className="text-white font-semibold text-lg hover:text-blue-400 transition-colors"
                >
                  (386) 775-1792
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock size={20} className="text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="text-gray-400">
                  <p className="text-white font-medium">Monday - Friday: 8:00 AM - 5:00 PM</p>
                  <p className="mt-1">After Hours (Telehealth Only): Monday - Friday, 5:00 PM - 7:00 PM</p>
                  <p className="text-xs text-gray-500 italic">(Please call the office to schedule)</p>
                  <p className="mt-1">Saturday: 8:00 AM - 1:00 PM</p>
                  <p className="text-xs text-gray-500 italic">(Available at both locations. Saturday availability varies throughout the year—please call the office to verify before visiting.)</p>
                </div>
              </li>
            </ul>
          </div>

          {/* DeLand Office */}
          <div>
            <h3 className="text-white font-bold text-lg mb-6 tracking-wide">DeLand Office</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={20} className="text-blue-400 mt-0.5 flex-shrink-0" />
                <address className="not-italic text-gray-400">
                  <span className="text-white font-medium">1190 N. Stone St.</span><br />
                  DeLand, FL 32720
                </address>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={20} className="text-blue-400 flex-shrink-0" />
                <a 
                  href="tel:386-738-1792" 
                  className="text-white font-semibold text-lg hover:text-blue-400 transition-colors"
                >
                  (386) 738-1792
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock size={20} className="text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="text-gray-400">
                  <p className="text-white font-medium">Monday - Friday: 8:00 AM - 5:00 PM</p>
                  <p className="mt-1">After Hours (Telehealth Only): Monday - Friday, 5:00 PM - 7:00 PM</p>
                  <p className="text-xs text-gray-500 italic">(Please call the office to schedule)</p>
                  <p className="mt-1">Saturday: 8:00 AM - 1:00 PM</p>
                  <p className="text-xs text-gray-500 italic">(Available at both locations. Saturday availability varies throughout the year—please call the office to verify before visiting.)</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Schedule CTA in Footer */}
        <div className="mt-12 pt-10 border-t border-gray-800">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <p className="text-white font-bold text-lg">Ready to Schedule an Appointment?</p>
              <p className="text-gray-400">Walk-ins welcome • Medicaid accepted</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setShowScheduleModal(true)}
                data-testid="footer-schedule-btn"
                className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors cursor-pointer"
              >
                <Calendar size={20} />
                Schedule Online
              </button>
              <a
                href="tel:386-775-1792"
                data-testid="footer-call-btn"
                className="inline-flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                <Phone size={20} />
                Call Now
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-gray-950 py-6 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm">
            <p className="text-gray-500">
              &copy; {new Date().getFullYear()} Community Medical Center of West Volusia. All rights reserved.
            </p>
            <p className="text-gray-500 text-center md:text-right">
              <span className="text-gray-400">Pediatrics:</span> Dr. Concepcion Anayas, M.D. • Pam Perry, ARNP
              <span className="mx-2">|</span>
              <span className="text-gray-400">Internal Medicine:</span> Dr. Marcelo Anayas, M.D.
            </p>
          </div>
          <div className="mt-4 pt-3 border-t border-gray-800/50 text-center">
            <a
              href="https://zdnmfsq42wgjpudqkuapp.ecwcloud.com/mobiledoc/jsp/webemr/login/newLogin.jsp"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="emr-login-link"
              className="text-gray-600 hover:text-gray-400 text-xs transition-colors"
            >
              Office Staff Only – EMR Login
            </a>
            <span className="text-gray-700 text-xs ml-2">· For CMC staff use only</span>
          </div>
        </div>
      </div>
    </footer>
    <ScheduleModal isOpen={showScheduleModal} onClose={() => setShowScheduleModal(false)} />
    </>
  );
};

export default Footer;
